import { AxiosRequestHeaders } from 'axios';
import { axios, AxiosPromise, AxiosRequestConfig, Method } from '../internal';
import { LinkType } from './types';

// Define an array of LinkTypes with rel typechecking, i.e. LinkArray<'self'|'delete'>
export type LinkArray<Rels extends string = string> = LinkType<Rels>[];

// Infer the generic rels passed into LinkArray, needed for getLink
type LinkArrayRels<T> = T extends LinkArray<infer Rels> ? Rels : T;

// Get a link from a LinkArray<Rels>, type error if rel not in Rels
export function getLink<Rels extends LinkArrayRels<T>, T extends LinkArray<any>>(
  rel: Rels,
  links?: T
) {
  return links?.find((link) => link.rel === rel);
}

// Common media types.  Non-exhaustive.
const MediaType = {
  SASCollection: 'application/vnd.sas.collection',
  SASSummary: 'application/vnd.sas.summary',
  JSON: 'application/json',
};

/**
 * Makes an Axios call with a SAS REST HATEOAS link object.
 * @param link Link object.
 * @param options Axios configuration overrides.
 * @returns Promise: Axios' response object.
 */
export function followAxiosLink<T>(link: LinkType, options?: AxiosRequestConfig): AxiosPromise<T> {
  const method = link.method.toLowerCase() as Method;
  const config: AxiosRequestConfig = {
    method,
  };

  // overwrite config with user values
  if (options) {
    Object.assign(config, options);
  }

  // make URI parameter substitutions, if necessary
  // (note: if config.url override exists, still modify config.params if applicable)
  let uri = link.uri;
  if (options && options.params) {
    const params = Object.assign({}, options.params); // don't modify original
    Object.keys(params).forEach((key) => {
      const pos = uri.indexOf(`{${key}}`);
      if (pos >= 0) {
        uri = uri.slice(0, pos) + encodeURIComponent(params[key]) + uri.slice(pos + key.length + 2);
        delete params[key];
      }
    });
    config.params = params;
  }

  // only overwrite the url if undefined
  if (!config.url) {
    config.url = uri;
  }

  // generate headers
  const headers: AxiosRequestHeaders = {};
  if (['get', 'delete'].includes(method)) {
    addJsonTypeToHeaders(headers, 'Accept', link.type, MediaType.JSON); // eslint-disable-line @sas/i18n/jsx-to-localize
    addJsonTypeToHeaders(headers, 'Accept-Item', link.itemType);
  } else if (['post', 'put', 'patch'].includes(method)) {
    addJsonTypeToHeaders(headers, 'Content-Type', link.type, MediaType.JSON);
    addJsonTypeToHeaders(headers, 'Content-Item-Type', link.itemType);
    addJsonTypeToHeaders(headers, 'Accept', link.responseType, MediaType.JSON); // eslint-disable-line @sas/i18n/jsx-to-localize
    addJsonTypeToHeaders(headers, 'Accept-Item', link.responseItemType);
  }

  // merge generated headers with user headers; user headers take precedence
  if (Object.keys(headers).length > 0) {
    if (options && options.headers) {
      Object.assign(headers, options.headers);
    }
    config.headers = headers;
  }

  // make the Axios call
  return axios(config);
}

// Adds '+json' to a type if not already present, then adds header to header map.
function addJsonTypeToHeaders(
  headers: AxiosRequestHeaders,
  key: string,
  type?: string,
  defaultType?: string
): void {
  if (type !== undefined) {
    // only append '+json' if 'json' doesn't already exist
    if (!type.toLowerCase().includes('json')) {
      type += '+json';
    }
    headers[key] = type;
  } else if (defaultType !== undefined) {
    headers[key] = defaultType;
  }
}
